/**
 * Housing Type Filter Service
 *
 * Matches a house plan/housing unit against the Filters modal's Housing type selection.
 * `isCustom`/`isLuxury` are independent boolean flags the gateway sends alongside `housingType`
 * (a plan can be `housingType: 'SingleFamily'` and `isCustom: true` at the same time — NHS20-9124),
 * so `'custom'`/`'luxury'` match on those flags directly, while `'singleFamily'`/`'condosAndTownHomes'`
 * match on the raw `housingType` sub type string the gateway sends (see
 * specs/005-native-interactive-site-map/contracts/lot-map-inventory.md).
 *
 * `'modelHomes'` is deliberately NOT matched here — model-home status lives on the lot
 * (`searchParameters.lotStatus`, the same thing the map chip bar drives), not on the plan/unit
 * itself, so `plugins/filtersModal.plugin.js` switches the chip instead of relying on this
 * function; every plan/unit that reaches here has therefore already been narrowed to model homes
 * upstream (via `selectMatchedLotIds`), and this always passes it through.
 *
 * @module nativeInteractiveSiteMap/services/housingTypeFilterService
 */

import HousingType from 'enums/housingType';

// Confirmed gateway values for `housingType`: 'SingleFamily' | 'Townhome' | 'Condominium' | 'Duplex'
// | 'NotSpecified'. 'Duplex' is deliberately left out of this set — whether it belongs under
// "Condos & townhomes" wasn't confirmed — so a Duplex plan/unit currently matches neither
// 'singleFamily' nor 'condosAndTownHomes' until that's decided.
const CONDOS_AND_TOWNHOMES_RAW_TYPES = new Set(['Townhome', 'Condominium']);

/**
 * @param {{ isCustom: boolean|null, isLuxury: boolean|null, housingType: string|null }} entity
 * @param {string|null} housingTypeKey - A `HousingType` key, or `null` for no filter.
 * @returns {boolean}
 */
export default function matchesHousingType(entity, housingTypeKey) {
  if (housingTypeKey == null) {
    return true;
  }

  // An unrecognized key falls through every case below (HousingType.fromKey returns undefined,
  // which matches none of them) and hits `default`, the same as the switch used to on a raw,
  // unrecognized string — not treated as "no filter" the way a null/undefined key is above.
  switch (HousingType.fromKey(housingTypeKey)) {
    case HousingType.Custom:
      return entity.isCustom === true;
    case HousingType.Luxury:
      return entity.isLuxury === true;
    case HousingType.SingleFamily:
      return entity.housingType === 'SingleFamily';
    case HousingType.CondosAndTownHomes:
      return CONDOS_AND_TOWNHOMES_RAW_TYPES.has(entity.housingType);
    case HousingType.ModelHomes:
      // Already narrowed to model homes upstream via the lot-status chip — see the module doc above.
      return true;
    default:
      return false;
  }
}
